import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import articles from './articles.json';
import './Blog.css';

const headingId = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

const getBlocks = (body, headings) => {
  const headingSet = new Set(headings);
  const lines = body.split(/\r?\n/);
  const blocks = [];
  let paragraph = [];
  let listItems = [];
  let listType = 'ul';

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ type: 'paragraph', text: paragraph.join(' ') });
      paragraph = [];
    }
  };

  const flushList = () => {
    if (listItems.length) {
      blocks.push({ type: listType, items: listItems });
      listItems = [];
    }
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trim();
    if (!line) {
      flushParagraph();
      flushList();
      return;
    }

    if (headingSet.has(line)) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'heading', text: line });
      return;
    }

    const listMatch = line.match(/^(?:([·•-])|(\d+\.))\s+(.+)$/);
    if (listMatch) {
      flushParagraph();
      const nextListType = listMatch[2] ? 'ol' : 'ul';
      if (listItems.length && nextListType !== listType) flushList();
      listType = nextListType;
      listItems.push(listMatch[3]);
      return;
    }

    flushList();
    paragraph.push(line);
  });

  flushParagraph();
  flushList();
  return blocks;
};

const renderInline = (text) => {
  const linkPattern = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;
  const pieces = [];
  let cursor = 0;
  let match;

  while ((match = linkPattern.exec(text)) !== null) {
    if (match.index > cursor) pieces.push(text.slice(cursor, match.index));
    pieces.push(
      <a href={match[2]} key={`${match.index}-${match[1]}`} target="_blank" rel="noreferrer">
        {match[1]}
      </a>
    );
    cursor = linkPattern.lastIndex;
  }

  if (!pieces.length) return text;
  if (cursor < text.length) pieces.push(text.slice(cursor));
  return pieces;
};

const ArticleCard = ({ article }) => (
  <article className="blog-card">
    <div className="blog-card-copy">
      <h2><Link to={`/blog/${article.slug}`}>{article.title}</Link></h2>
      <p>{article.excerpt}</p>
      <Link className="blog-text-link" to={`/blog/${article.slug}`}>
        Read article
      </Link>
    </div>
  </article>
);

const BlogIndex = () => (
  <div className="blog-page">
    <section className="blog-title-section">
      <div className="blog-title-decoration" aria-hidden="true">
        <span className="blog-title-circle blog-title-circle--navy" />
        <span className="blog-title-circle blog-title-circle--blue" />
        <span className="blog-title-circle blog-title-circle--peach" />
      </div>
      <div className="blog-title-content"><h1>Our Blog</h1></div>
    </section>

    <section className="blog-library" id="latest-articles">
      <div className="blog-card-grid">
        {articles.map((article) => (
          <ArticleCard article={article} key={article.slug} />
        ))}
      </div>
    </section>
  </div>
);

const ArticlePage = ({ article }) => {
  const blocks = getBlocks(article.body, article.headings);
  const relatedArticles = articles.filter((item) => item.slug !== article.slug);

  return (
    <div className="blog-page blog-page--article">
      <div className={`article-hero article-hero--${article.slug}`}>
        <nav className="article-breadcrumbs" aria-label="Breadcrumb">
          <Link to="/">Home</Link><span>/</span><Link to="/blog">Our Blog</Link>
        </nav>
        <div className="article-hero-content">
          <div className="article-hero-copy">
            <h1>{article.title}</h1>
            <p className="article-deck">{article.excerpt}</p>
          </div>
        </div>
      </div>

      <div className="article-layout">
        <article className="article-body">
          <Link className="article-back-link" to="/blog">Back to all articles</Link>
          {blocks.map((block, index) => {
            if (block.type === 'heading') {
              return <h2 id={headingId(block.text)} key={`${block.type}-${index}`}>{block.text}</h2>;
            }
            if (block.type === 'ol' || block.type === 'ul') {
              const List = block.type;
              return (
                <List key={`${block.type}-${index}`}>
                  {block.items.map((item, itemIndex) => <li key={itemIndex}>{renderInline(item)}</li>)}
                </List>
              );
            }
            return <p key={`${block.type}-${index}`}>{renderInline(block.text)}</p>;
          })}
        </article>

        <aside className="article-more">
          <h2>More from the blog</h2>
          {relatedArticles.map((item) => (
            <Link className="article-more-link" to={`/blog/${item.slug}`} key={item.slug}>
              {item.title}
            </Link>
          ))}
          <Link className="article-more-all" to="/blog">View all articles</Link>
        </aside>
      </div>
    </div>
  );
};

const Blog = () => {
  const { slug } = useParams();
  const article = articles.find((item) => item.slug === slug);

  useEffect(() => {
    const pageTitle = article ? `${article.title} | Peak Mobile` : 'Our Blog | Peak Mobile';
    const description = article ? article.excerpt : 'Practical perspectives on messaging, loyalty and building stronger customer relationships.';
    document.title = pageTitle;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) metaDescription.setAttribute('content', description);

    return () => {
      document.title = 'Peak Mobile';
      if (metaDescription) metaDescription.setAttribute('content', 'Peak Mobile Website');
    };
  }, [article]);

  if (slug && !article) {
    return (
      <div className="blog-page blog-not-found">
        <p className="blog-section-kicker">Article not found</p>
        <h1>That page has moved on.</h1>
        <p>Explore the latest ideas from Peak Mobile instead.</p>
        <Link className="blog-cta-button" to="/blog">Back to the blog</Link>
      </div>
    );
  }

  return article ? <ArticlePage article={article} /> : <BlogIndex />;
};

export default Blog;
